import * as faceapi from "@vladmandic/face-api";

let modelsLoaded = false;

// Modelos locales en /models (Docker build) o CDN como respaldo
const MODEL_URL =
  process.env.REACT_APP_FACE_MODEL_URL ||
  `${process.env.PUBLIC_URL || ""}/models`;
const MODEL_URL_FALLBACK = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.14/model";

// Distancia maxima para considerar una coincidencia "fuerte" y registrar directo.
export const STRONG_THRESHOLD = 0.48;
// Distancia maxima para considerar que existe algun candidato plausible.
export const LOOSE_THRESHOLD = 0.55;
// Diferencia minima entre el mejor y el segundo candidato para no ser ambiguo.
export const MARGIN_MIN = 0.06;
// Compatibilidad: umbral historico usado por llamadas antiguas.
export const FACE_MATCH_THRESHOLD = LOOSE_THRESHOLD;

// Calidad minima del rostro detectado en video.
const MIN_DETECTION_SCORE = 0.55;
// Lado minimo del rostro respecto al lado menor del frame (evita rostros lejanos/borrosos).
const MIN_FACE_RATIO = 0.18;

const DETECTOR_OPTIONS = new faceapi.TinyFaceDetectorOptions({ inputSize: 416 });

async function loadAllNets(baseUrl) {
  await Promise.all([
    faceapi.nets.tinyFaceDetector.loadFromUri(baseUrl),
    faceapi.nets.faceLandmark68Net.loadFromUri(baseUrl),
    faceapi.nets.faceRecognitionNet.loadFromUri(baseUrl),
  ]);
}

export async function loadFaceModels() {
  if (modelsLoaded) return;
  try {
    await loadAllNets(MODEL_URL);
  } catch (primaryErr) {
    console.warn("Modelos faciales locales no disponibles, usando CDN:", primaryErr);
    await loadAllNets(MODEL_URL_FALLBACK);
  }
  modelsLoaded = true;
}

export async function descriptorFromDataUrl(dataUrl) {
  if (!dataUrl) return null;
  await loadFaceModels();
  const img = await faceapi.fetchImage(dataUrl);
  const det = await faceapi
    .detectSingleFace(img, DETECTOR_OPTIONS)
    .withFaceLandmarks()
    .withFaceDescriptor();
  if (!det) return null;
  return Array.from(det.descriptor);
}

/**
 * Calcula los descriptores de varias imagenes (varios angulos del rostro).
 * Devuelve solo los descriptores validos (omite imagenes sin rostro detectable).
 */
export async function descriptorsFromDataUrls(dataUrls) {
  const list = (dataUrls || []).filter(Boolean);
  const out = [];
  for (const url of list) {
    try {
      const desc = await descriptorFromDataUrl(url);
      if (desc) out.push(desc);
    } catch {
      /* imagen invalida: se omite */
    }
  }
  return out;
}

/** Lista de descriptores de referencia de un suscriptor (soporta array o legacy unico). */
function refDescriptors(entry) {
  const many = entry?.face_descriptors;
  if (Array.isArray(many) && many.length) {
    return many.filter((d) => Array.isArray(d) && d.length);
  }
  const one = entry?.face_descriptor;
  return Array.isArray(one) && one.length ? [one] : [];
}

/** Distancia minima del probe contra todas las referencias del suscriptor. */
function bestDistanceToEntry(probe, entry) {
  let best = Infinity;
  for (const ref of refDescriptors(entry)) {
    const dist = faceapi.euclideanDistance(probe, new Float32Array(ref));
    if (dist < best) best = dist;
  }
  return best;
}

/**
 * Busca al mejor candidato y evalua la confianza comparando con el segundo mejor.
 * Devuelve { subscriber, distance, secondDistance, margin, status, runnerUp }
 * status: "confident" | "ambiguous" | "none".
 */
export function evaluateMatch(descriptor, catalog) {
  if (!descriptor?.length || !catalog?.length) {
    return { subscriber: null, distance: Infinity, secondDistance: Infinity, margin: 0, status: "none", runnerUp: null };
  }
  const probe = new Float32Array(descriptor);
  let best = null;
  let bestDist = Infinity;
  let second = null;
  let secondDist = Infinity;

  for (const entry of catalog) {
    if (!refDescriptors(entry).length) continue;
    const dist = bestDistanceToEntry(probe, entry);
    if (dist < bestDist) {
      second = best;
      secondDist = bestDist;
      best = entry;
      bestDist = dist;
    } else if (dist < secondDist) {
      second = entry;
      secondDist = dist;
    }
  }

  const margin = Number.isFinite(secondDist) ? secondDist - bestDist : Infinity;

  let status = "none";
  if (best && bestDist <= LOOSE_THRESHOLD) {
    if (bestDist <= STRONG_THRESHOLD && margin >= MARGIN_MIN) {
      status = "confident";
    } else {
      status = "ambiguous";
    }
  }

  return {
    subscriber: status === "none" ? null : best,
    distance: bestDist,
    secondDistance: secondDist,
    margin,
    status,
    runnerUp: status === "ambiguous" ? second : null,
  };
}

/**
 * Compatibilidad: devuelve { subscriber, distance } solo cuando hay coincidencia
 * (fuerte o ambigua) por debajo del umbral indicado.
 */
export function findBestMatch(descriptor, catalog, threshold = LOOSE_THRESHOLD) {
  const res = evaluateMatch(descriptor, catalog);
  if (!res.subscriber || res.distance > threshold) return null;
  return { subscriber: res.subscriber, distance: res.distance };
}

/**
 * Extrae el descriptor del frame de video aplicando control de calidad:
 * exige un unico rostro, score minimo y tamano minimo respecto al frame.
 */
export async function descriptorFromVideo(videoEl) {
  await loadFaceModels();
  const detections = await faceapi
    .detectAllFaces(videoEl, DETECTOR_OPTIONS)
    .withFaceLandmarks()
    .withFaceDescriptors();

  if (!detections.length) return null;
  // Mas de un rostro en cuadro: ambiguo, no arriesgar identificacion.
  if (detections.length > 1) return null;

  const det = detections[0];
  const score = det.detection?.score ?? 0;
  if (score < MIN_DETECTION_SCORE) return null;

  const box = det.detection?.box;
  const frameW = videoEl.videoWidth || box?.width || 1;
  const frameH = videoEl.videoHeight || box?.height || 1;
  const minSide = Math.min(frameW, frameH);
  const faceSide = Math.min(box?.width || 0, box?.height || 0);
  if (minSide > 0 && faceSide / minSide < MIN_FACE_RATIO) return null;

  return Array.from(det.descriptor);
}

/** Captura el frame actual del video como data URL JPEG (para verificacion con IA). */
export function captureFrameDataUrl(videoEl, quality = 0.85) {
  if (!videoEl || !videoEl.videoWidth) return null;
  const canvas = document.createElement("canvas");
  canvas.width = videoEl.videoWidth;
  canvas.height = videoEl.videoHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
  try {
    return canvas.toDataURL("image/jpeg", quality);
  } catch {
    return null;
  }
}
